import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./StreamsPage.css";
import { ReactComponent as LeftArrow } from "../images/LeftArrow.svg";
import { ReactComponent as RightArrow } from "../images/RightArrow.svg";
import iHubLogo from "../images/ILH-HORIZONTAL-FULL-COLOR.jpg";
import config from "../config/config";

const StreamsPage = ({ label, checked, onChange }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("AFS");
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem("currentPage");
    return savedPage ? parseInt(savedPage, 10) : 1; // Load saved page or default to 1
  });
  const [totalPages, setTotalPages] = useState(1);
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const savedInterval = sessionStorage.getItem("refreshInterval");
    return savedInterval ? parseInt(savedInterval, 10) : 15;
  });
  const [showErrorsOnly, setShowErrorsOnly] = useState(() => {
    return sessionStorage.getItem("showErrorsOnly") === "true";
  });

  const rowsPerPage = 8;
  const navigate = useNavigate();

  // Helper Function: Format Date | Time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";
    const date = new Date(dateTime);
    const formattedDate = date.toLocaleDateString(); // Format as MM/DD/YYYY or based on locale
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }); // Format as HH:MM AM/PM
    return `${formattedDate} | ${formattedTime}`;
  };

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/default/Projects`
        );
        const projectData = Array.isArray(response.data) ? response.data : [];
        setProjects(projectData);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      }
    };

    fetchProjects();
  }, []);

  // Fetch streams from API
  useEffect(() => {
    if (!selectedProject) return;

    const fetchStreams = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/default/Streams/?ProjectCode=${selectedProject}&Period1=Week&Period2=Day`
        );
        const streamData = Array.isArray(response.data) ? response.data : [];
        const filteredData = streamData.filter(
          (stream) => stream.ProjectCode === selectedProject
        );

        // Only include streams with errors when "Show Errors Only" is checked
        const filteredWithErrors = showErrorsOnly
          ? filteredData.filter(
              (stream) =>
                stream.Period1ErrorTotal > 0 || stream.Period2ErrorTotal > 0
            )
          : filteredData;

        setFilteredStreams(filteredWithErrors);

        // Update total pages based on the filtered data
        const total = Math.ceil(filteredWithErrors.length / rowsPerPage);
        setTotalPages(total);

        // Reset to first page when filter changes
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching streams:", error);
        setFilteredStreams([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchStreams();

    // Auto-refresh based on interval
    const interval = setInterval(fetchStreams, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [selectedProject, refreshInterval, showErrorsOnly]); // Depend on errors-only filter

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredStreams.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      sessionStorage.setItem("currentPage", newPage); // Save current page to sessionStorage
    }
  };

  const handleStreamRunsNavigation = (streamId, period) => {
    navigate(`/stream-runs/${streamId}/${period}`);
  };

  const handleRefreshIntervalChange = (value) => {
    const interval = parseInt(value, 10);
    setRefreshInterval(interval);
    sessionStorage.setItem("refreshInterval", interval); // Save to sessionStorage
  };

  const handleShowErrorsOnlyChange = () => {
    const newValue = !showErrorsOnly;
    setShowErrorsOnly(newValue);
    sessionStorage.setItem("showErrorsOnly", newValue);
  };

  return (
    <div className="streamsPage-layout">
      <div className="streamsPage-header">
        <div className="streamsPage-logo">
          <img src={iHubLogo} alt="iHubLogo" />
        </div>
        <h1 className="streamsPage-title">iHub Streams</h1>
      </div>

      <div className="streamsPage-infoContainer">
        <div className="streamsPage-filters">
          <div className="streamsPage-filterGroup">
            <label htmlFor="project-name" className="streamsPage-filterLabel">
              Customer Name
            </label>
            <select
              className="streamsPage-dropDown"
              id="project-name"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">Select customer name</option>
              {Array.isArray(projects) &&
                projects.map((project) => (
                  <option key={project.Id} value={project.Code}>
                    {project.Name} ({project.Code})
                  </option>
                ))}
            </select>
          </div>

          {/* Show Only Errors */}
          <div className="streamsPage-filterGroup2">
            <label className="custom-checkbox">
              <input
                type="checkbox"
                checked={showErrorsOnly}
                onChange={handleShowErrorsOnlyChange}
                className="custom-checkbox-input"
              />

              <span className="custom-checkbox-box"></span>
              <p className="custom-checkbox-text">Show Only Errors</p>
              {label && <span className="custom-checkbox-label">{label}</span>}
            </label>

            {/* Refresh Timer Dropdown */}
            <div className="streamsPage-filterGroup">
              <label
                htmlFor="refresh-timer"
                className="streamsPage-filterLabel"
              >
                Refresh (Every)
              </label>
              <select
                className="streamsPage-dropDown"
                id="refresh-timer"
                value={refreshInterval}
                onChange={(e) => handleRefreshIntervalChange(e.target.value)}
              >
                <option value="15">15 Secs</option>
                <option value="30">30 Secs</option>
                <option value="60">1 min</option>
                <option value="300">5 mins</option>
                <option value="600">10 mins</option>
                <option value="900">15 mins</option>
                <option value="1800">30 mins</option>
                <option value="3600">1 hour</option>
              </select>
            </div>
          </div>
        </div>

        <div className="streamsPage-tableContainer">
          {loading ? (
            <div className="streamPage-loading">
              <div className="spinner"></div>
              Loading Data...
            </div>
          ) : (
            <table className="streamsPage-table">
              <thead>
                <tr>
                  <th>Stream Name</th>
                  <th>Last Run Time</th>
                  <th>Last Run Duration</th>
                  <th>Last Week</th>
                  <th>Last 24 Hours</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(currentRows) && currentRows.length > 0 ? (
                  currentRows.map((stream) => (
                    <tr key={stream.Id}>
                      <td>{stream.StreamName}</td>
                      <td>{formatDateTime(stream.LastRunDateTime)}</td>
                      <td>{stream.LastRunDuration} s</td>
                      <td>
                        <button
                          className="streamsPage-link"
                          onClick={() =>
                            handleStreamRunsNavigation(stream.Id, "Week")
                          }
                        >
                          {stream.Period1Total}/
                          <span className="streamsPage-error">
                            {stream.Period1ErrorTotal}
                          </span>
                        </button>
                      </td>
                      <td>
                        <button
                          className="streamsPage-link"
                          onClick={() =>
                            handleStreamRunsNavigation(stream.Id, "Day")
                          }
                        >
                          {stream.Period2Total}/
                          <span className="streamsPage-error">
                            {stream.Period2ErrorTotal}
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">No streams available for this project.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {!loading && totalPages > 1 && (
          <div className="streamsPage-pagination">
            <button
              className="streamsPage-paginationButtonArrow"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <LeftArrow className="streamsPage-arrowIcon" />
            </button>

            {/* Display page numbers */}
            {Array.from({ length: 5 }).map((_, index) => {
              let pageNumber;

              // If we're near the first few pages, show the first 5 pages
              if (currentPage <= 3) {
                pageNumber = index + 1;
              }
              // If we're near the last few pages, show the last 5 pages
              else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 5 + index;
              }
              // Otherwise, show 5 pages centered around the current page
              else {
                pageNumber = currentPage - 2 + index;
              }

              // Skip invalid page numbers (outside the valid range)
              if (pageNumber < 1 || pageNumber > totalPages) {
                return null;
              }

              return (
                <button
                  key={pageNumber}
                  className={`streamsPage-paginationButton ${
                    currentPage === pageNumber ? "active" : ""
                  }`}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              className="streamsPage-paginationButtonArrow"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <RightArrow className="streamsPage-arrowIcon" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamsPage;
